import useSWR from 'swr';
import { SearchModes } from '@/domainLogic/index.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSearchParams } from 'next/navigation';
import { URLParameters } from '@/app/common/urlParameters';
import {
  isValidFlightClass,
  getDestinationParameters,
  isValidHotelId,
  buildAccommodationInterval,
} from '@/app/components/hotel-detail/common/common.helper';
import { fetchWithTracingLoggingAndRetryOnce } from '@/app/components/hotel-detail/common/fetchWithTracing';
import { getLoggingInfo } from '@/app/components/hotel-detail/lib/loggingInfo';
import { BASE_PATH } from '@/app/components/search/utils/path';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import useUserConfig from './useUserConfig';
import { useCreateAsyncPricing } from '../PricingProvider';
import { useInitialParams } from '../InitialParamsProvider';

export function useFetchRooms() {
  const { internalHotelId, pageType, priceType } = useInitialParams();
  const {
    searchInfoData,
    searchInfoDetailData: { destination },
    localization,
    commonData,
  } = usePageWrapperData();
  const { isApp } = commonData;
  const { destinationType, id: destinationId } = destination;
  const { data: asyncPricingData } = useCreateAsyncPricing();
  const { showBackToTravel, isDoublePrice, isDoublePriceDP } = useUserConfig();
  const { checkin, checkout, travellerGroups, accommodationOnly, totalGuestsWithoutInfants } = useUrlSearchParams();
  const searchParams = useSearchParams();
  const departureAirport = searchParams.get(URLParameters.ORIGIN);
  const koProviderId = searchParams.get(URLParameters.KOPROVIDER_ID);
  const feedContext = searchParams.get(URLParameters.FEED_CONTEXT);
  const airline = searchParams.get(URLParameters.AIRLINE);
  const stops = searchParams.get(URLParameters.STOPS);
  const direct = searchParams.get(URLParameters.DIRECT);
  const flightClass = searchParams.get(URLParameters.FLIGHT_CLASS) ?? undefined;
  const transportId = searchParams.get(URLParameters.TRANSPORT_ID);
  const enforceProvider = searchParams.get(URLParameters.PROVIDER);
  const internalIdTransport = searchParams.get(URLParameters.INTERNAL_ID_TRANSPORT);

  const formattedCheckin = checkin.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
  const formattedCheckout = checkout.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
  const accommodation = buildAccommodationInterval(
    searchParams.get(URLParameters.CUSTOM_CHECKIN_CHECKOUT),
    searchInfoData.searchMode === SearchModes.DP
  );

  const asyncRequest = {
    request: {
      businessProfileId: localization.businessProfile,
      ...(!accommodationOnly && departureAirport ? { departure: { airports: [departureAirport] } } : {}),
      destination: {
        ...getDestinationParameters(destinationType, destinationId),
        hotelIds: [internalHotelId],
      },
      interval: { start: { date: formattedCheckin }, end: { date: formattedCheckout } },
      ...(accommodation ? { accommodation } : {}),
      travellerGroups,
      currency: localization.currency,
      mobile: isApp && accommodationOnly,
      ...(koProviderId || enforceProvider
        ? {
            providers: {
              ...(koProviderId ? { excluded: [Number(koProviderId)] } : undefined),
              ...(enforceProvider ? { enforced: Number(enforceProvider) } : undefined),
            },
          }
        : undefined),
      ...(transportId || internalIdTransport
        ? {
            transport: {
              ...(transportId ? { id: transportId } : {}),
              ...(internalIdTransport ? { internalIdTransport } : {}),
            },
          }
        : isValidFlightClass(flightClass) || stops || airline
          ? {
              transport: {
                preferred: {
                  ...(isValidFlightClass(flightClass) ? { class: flightClass } : {}),
                  ...(direct ? (direct === '1' ? { directOnly: true } : { directOnly: false }) : {}),
                  ...(airline ? { airline } : {}),
                },
              },
            }
          : {}),
      ...(feedContext
        ? {
            additionalData: {
              ...(feedContext ? { feedContext } : {}),
            },
          }
        : {}),
    },
    pageType,
    priceType,
    accommodationOnly,
    flashSalesRequireDiscount: localization.configValuesApp.flashSalesRequireDiscount,
    showBackToTravel,
    doublePrice: isDoublePrice || isDoublePriceDP,
    pricingId: asyncPricingData?.pricingId || '',
    totalGuestsWithoutInfants,
    debugMode: true,
    locale: localization.locale,
    country: localization.country,
    lang: localization.lang,
  };

  const key: string | undefined =
    isValidHotelId(internalHotelId) && asyncPricingData?.pricingId ? asyncPricingData.pricingId : undefined;
  return useSWR<RoomsServiceResponseType | RoomsServiceResponseErrorType>(
    key,
    async () =>
      // A throw here lands on the terminal ERROR_FETCHING_ROOMS path in OffersProvider (the RETRY_ORDER
      // machinery only handles 2xx error payloads). Retry once, inside the fetcher, network drops only —
      // see fetchWithTracingLoggingAndRetryOnce for why not SWR-level and why statuses stay terminal.
      fetchWithTracingLoggingAndRetryOnce<RoomsServiceResponseType | RoomsServiceResponseErrorType>(
        `${BASE_PATH}/api/hotel-detail/rooms`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({ ...asyncRequest }),
        },
        getLoggingInfo(pageType, 'rooms-api', 'HotelPageRoomSection'),
        searchInfoData.searchTraceParent,
        commonData
      ),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      dedupingInterval: 1000 * 60 * 30,
    }
  );
}
